import type { AuditLogEntry } from '@wf-coe/types';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useApiClient } from '../lib/use-api-client.js';

/**
 * Wire-format audit-log entry — the API serializes `createdAt` as an
 * ISO string, not a real `Date`. The Dashboard widget renders it
 * directly with `Date.parse`/`Intl.RelativeTimeFormat` so we keep the
 * string here rather than re-coercing on every fetch.
 */
export type AuditLogEntryDto = Omit<AuditLogEntry, 'createdAt'> & {
  createdAt: string;
};

export interface AuditLogQueryArgs {
  /** Tenant id; omit for the global feed (Admin only). */
  clientId?: string;
  /** Page size. Defaults to 25 — small enough for a dashboard widget. */
  limit?: number;
}

/**
 * TanStack Query hook for `GET /v1/audit-log`. Casbin
 * `(audit-log, read)` on the server gates Admin / Operator / Viewer.
 * Without a `clientId`, only Admin succeeds — Operator/Viewer would
 * leak cross-tenant data via the global feed, so the server returns
 * 403 in that case (surfaced here as an `ApiError`).
 *
 * Re-fetches every 30 s so the dashboard stays fresh while open. The
 * `before` cursor for paging is left as a follow-up — the widget
 * shows "newest 25" only.
 */
export function useAuditLogQuery(args: AuditLogQueryArgs = {}): UseQueryResult<AuditLogEntryDto[]> {
  const api = useApiClient();
  const limit = args.limit ?? 25;
  const params = new URLSearchParams();
  if (args.clientId) params.set('clientId', args.clientId);
  params.set('limit', String(limit));
  const search = params.toString();
  return useQuery({
    queryKey: ['audit-log', { clientId: args.clientId ?? null, limit }],
    queryFn: () => {
      if (!api) throw new Error('useAuditLogQuery: api client unavailable');
      return api.get<AuditLogEntryDto[]>(`/v1/audit-log?${search}`);
    },
    enabled: api !== undefined,
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}
